import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import { DashboardRefreshProvider } from "@/hooks/useDashboardRefresh";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import { PackageDataProvider } from "@/providers/PackageDataProvider";
import PasswordResetHandler from "@/components/PasswordResetHandler";
import { RouteLoadingFallback } from "@/components/RouteLoadingFallback";

// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CustomerDashboard = lazy(() => import("./pages/CustomerDashboard"));
const CampaignBuilderPage = lazy(() => import("./pages/CampaignBuilder"));
const ServiceBuilderPage = lazy(() => import("./pages/ServiceBuilder"));
const AIAutomationDashboard = lazy(() => import("./components/AIAutomationDashboard"));
const AdminProtectedRoute = lazy(() => import("./components/AdminProtectedRoute").then(mod => ({ default: mod.AdminProtectedRoute })));
const NotFound = lazy(() => import("./pages/NotFound"));
const Legal = lazy(() => import("./pages/Legal"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
 
 const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <AuthProvider>
        <DashboardRefreshProvider>
          <PackageDataProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <PasswordResetHandler />
              <AnalyticsTracker>
                <Suspense fallback={<RouteLoadingFallback />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/referral/:code" element={<ReferralPage />} />
                    <Route path="/app/*" element={<CustomerDashboard />} />
                    <Route path="/dashboard/*" element={<CustomerDashboard />} />
                    <Route path="/admin" element={<Dashboard />} />
                    <Route path="/service-builder" element={<ServiceBuilderPage />} />
                    <Route path="/admin/ai-automation" element={
                      <AdminProtectedRoute>
                        <AIAutomationDashboard onBack={() => window.history.back()} />
                      </AdminProtectedRoute>
                    } />
                    <Route path="/campaign-builder" element={<CampaignBuilderPage />} />
                    <Route path="/legal" element={<Legal />} />
                    <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/legal/terms-of-service" element={<TermsOfService />} />
                    <Route path="/legal/cookie-policy" element={<CookiePolicy />} />
                    <Route path="/legal/refund-policy" element={<RefundPolicy />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </AnalyticsTracker>
            </TooltipProvider>
          </PackageDataProvider>
        </DashboardRefreshProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
